import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Phone, MessageCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const ServiceTemplate = ({ 
  title, 
  subtitle, 
  description, 
  heroImage, 
  features, 
  benefits, 
  process,
  gallery = [],
  metaKeywords = ''
}) => {
  return (
    <div className="min-h-screen pt-20" data-testid={`service-page-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" data-testid="service-hero">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#003B7A]/90 via-[#003B7A]/70 to-[#003B7A]/50"></div>
        </div>
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 py-24 text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="font-body text-xs tracking-[0.3em] uppercase text-[#FFD700] mb-4 md:mb-6" data-testid="service-subtitle">
              {subtitle}
            </p>
            <h1 className="font-heading font-extrabold text-2xl sm:text-3xl md:text-5xl lg:text-7xl tracking-tight leading-[1.1] mb-6 md:mb-8" data-testid="service-title">
              {title}
            </h1>
            <p className="font-body text-base md:text-lg lg:text-xl leading-relaxed text-gray-300 mb-8 md:mb-12" data-testid="service-description">
              {description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                className="bg-[#FFD700] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-[#c19d2f] transition-all duration-300 shadow-lg text-center"
                data-testid="service-hero-cta"
              >
                Jetzt Angebot anfordern
              </Link>
              <a
                href="tel:+41792649063"
                className="bg-transparent border-2 border-white text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-white hover:text-[#003B7A] transition-all duration-300 text-center flex items-center justify-center gap-2"
                data-testid="service-hero-phone"
              >
                <Phone className="w-5 h-5" />
                +41 79 264 90 63
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      {features && features.length > 0 && (
        <section className="py-24 md:py-32 bg-white" data-testid="service-features">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6">
                Unsere Leistungen
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#F5F5F7] p-8"
                  data-testid={`feature-${index}`}
                >
                  <CheckCircle2 className="w-10 h-10 text-[#FFD700] mb-4" />
                  <h3 className="font-heading font-semibold text-xl mb-3">{feature.title}</h3>
                  <p className="font-body text-sm text-gray-700 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {benefits && benefits.length > 0 && (
        <section className="py-24 md:py-32 bg-[#F5F5F7]" data-testid="service-benefits">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <p className="font-body text-xs tracking-[0.3em] uppercase text-[#FFD700] mb-4">
                  Ihre Vorteile
                </p>
                <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-8">
                  Warum Marali Immobilien AG?
                </h2>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-4" data-testid={`benefit-${index}`}>
                      <CheckCircle2 className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-1" />
                      <span className="font-body text-base text-gray-700 leading-relaxed">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <img 
                  src={gallery[0] || heroImage}
                  alt="Quality Work"
                  className="w-full h-[500px] object-cover shadow-2xl"
                />
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Process Section */}
      {process && process.length > 0 && (
        <section className="py-24 md:py-32 bg-white" data-testid="service-process">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6">
                Unser Vorgehen
              </h2>
              <p className="font-body text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
                Von der ersten Beratung bis zur finalen Abnahme – wir begleiten Sie professionell durch jeden Schritt.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {process.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                  data-testid={`process-step-${index}`}
                >
                  <div className="bg-[#F5F5F7] p-8 h-full">
                    <div className="w-12 h-12 bg-[#FFD700] text-white rounded-full flex items-center justify-center font-heading font-bold text-xl mb-6">
                      {index + 1}
                    </div>
                    <h3 className="font-heading font-semibold text-xl mb-4">{step.title}</h3>
                    <p className="font-body text-sm text-gray-700 leading-relaxed">{step.description}</p>
                  </div>
                  {index < process.length - 1 && (
                    <ArrowRight className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-[#FFD700] w-8 h-8" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {gallery && gallery.length > 0 && (
        <section className="py-24 md:py-32 bg-[#F5F5F7]" data-testid="service-gallery">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6">
                Unsere Referenzen
              </h2>
              <p className="font-body text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
                Einblicke in unsere erfolgreich abgeschlossenen Projekte.
              </p>
            </motion.div>

            <div className={`grid gap-4 ${gallery.length === 1 ? 'grid-cols-1 max-w-2xl mx-auto' : gallery.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {gallery.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="overflow-hidden shadow-lg"
                  data-testid={`gallery-image-${index}`}
                >
                  <img 
                    src={image}
                    alt={`${title} Projekt ${index + 1}`}
                    className="w-full h-64 md:h-80 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-[#003B7A] text-white" data-testid="service-cta">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6">
              Bereit für perfekte Ergebnisse?
            </h2>
            <p className="font-body text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Kontaktieren Sie uns für eine kostenlose Beratung und ein unverbindliches Angebot.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="bg-[#FFD700] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-[#c19d2f] transition-all duration-300 shadow-lg"
                data-testid="service-cta-contact"
              >
                Kostenloses Angebot sichern
              </Link>
              <a
                href="https://wa.me/41792649063"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-[#1ea952] transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="service-cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp schreiben
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceTemplate;
